import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { seyirViolationService } from '../services/seyirViolationService';

type Violation = {
  id: number;
  violation_type: string;
  severity: string;
  first_detected_at: string;
  last_detected_at: string;
  check_count: number;
  tractor_plate: string | null;
  trailer_plate: string | null;
  system_driver_name: string | null;
  matched_driver_name: string | null;
  description: string | null;
  driver_viewed_at: string | null;
};

const labels: Record<string, string> = {
  speed_exceeded: 'Hız limiti ihlali',
  idle_exceeded: 'Rölanti aşımı',
  sudden_fuel_drop: 'Ani yakıt düşüşü',
  unknown_digital_license: 'Tanımsız / geçersiz sürücü kartı',
  assigned_driver_mismatch: 'Atanan sürücü-kart uyumsuzluğu',
  missing_driver_code_moving: 'Kartsız sürüş (depo dışı hareket)',
};

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('tr-TR');
};

const getCategory = (violation: Violation) => {
  if (violation.violation_type === 'speed_exceeded' || violation.violation_type === 'idle_exceeded') return 'operational';
  return 'severe';
};

const getLabel = (violation: Violation) => {
  if (violation.violation_type.startsWith('tachograph_alert_')) {
    return `Takograf alarmı (${violation.violation_type.replace('tachograph_alert_', '')})`;
  }
  return labels[violation.violation_type] || violation.violation_type;
};

export function DriverViolations() {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState<Violation[]>([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [category, setCategory] = useState<'all' | 'operational' | 'severe'>('all');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const response: any = await seyirViolationService.getViolations({ startDate, endDate, limit: 1000 });
      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (error: any) {
      toast.error(error?.message || 'İhlal kayıtları alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [startDate, endDate]);

  const filteredRows = useMemo(
    () => rows.filter((row) => category === 'all' || getCategory(row) === category),
    [rows, category]
  );
  const operationalCount = rows.filter((row) => getCategory(row) === 'operational').length;
  const severeCount = rows.filter((row) => getCategory(row) === 'severe').length;

  const scan = async () => {
    try {
      setScanning(true);
      const response: any = await seyirViolationService.scanNow();
      toast.success(response?.message || 'Seyir ihlal taraması tamamlandı');
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Seyir ihlal taraması çalıştırılamadı');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900"><ShieldAlert className="h-6 w-6 text-rose-600" /><h1 className="text-2xl font-bold">Sürücü İhlalleri</h1></div>
          <p className="mt-1 text-sm text-slate-500">Özmal sürücüler için Seyir telemetri ve takograf alarm kayıtları</p>
        </div>
        <button type="button" onClick={() => void scan()} disabled={scanning} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} /> {scanning ? 'Taranıyor...' : 'Şimdi Tara'}
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <button type="button" onClick={() => setCategory('all')} className={`rounded-2xl border p-4 text-left ${category === 'all' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-900'}`}><div className="text-sm opacity-75">Toplam Kayıt</div><div className="mt-1 text-3xl font-bold">{rows.length}</div></button>
        <button type="button" onClick={() => setCategory('operational')} className={`rounded-2xl border p-4 text-left ${category === 'operational' ? 'border-amber-600 bg-amber-600 text-white' : 'border-amber-200 bg-amber-50 text-amber-950'}`}><div className="text-sm opacity-75">Operasyonel İhlaller</div><div className="mt-1 text-3xl font-bold">{operationalCount}</div></button>
        <button type="button" onClick={() => setCategory('severe')} className={`rounded-2xl border p-4 text-left ${category === 'severe' ? 'border-rose-700 bg-rose-700 text-white' : 'border-rose-200 bg-rose-50 text-rose-950'}`}><div className="text-sm opacity-75">Ağır İhlaller</div><div className="mt-1 text-3xl font-bold">{severeCount}</div></button>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <label className="text-sm font-medium text-slate-700">Başlangıç<input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="mt-1 block rounded-lg border border-slate-300 px-3 py-2" /></label>
        <label className="text-sm font-medium text-slate-700">Bitiş<input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="mt-1 block rounded-lg border border-slate-300 px-3 py-2" /></label>
        <button type="button" onClick={() => void load()} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Filtrele</button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Kategori</th><th className="px-4 py-3">İhlal</th><th className="px-4 py-3">Sürücü / Araç</th><th className="px-4 py-3">Ölçüm ve Değerlendirme</th><th className="px-4 py-3">Son Tespit</th><th className="px-4 py-3">Savunma Okundu</th><th className="px-4 py-3">Tekrar</th></tr></thead><tbody className="divide-y divide-slate-100">{loading ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td></tr> : filteredRows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Seçilen tarih aralığında ihlal kaydı yok.</td></tr> : filteredRows.map((row) => <tr key={row.id}><td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getCategory(row) === 'severe' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{getCategory(row) === 'severe' ? 'Ağır' : 'Operasyonel'}</span></td><td className="px-4 py-3 font-medium text-slate-900">{getLabel(row)}</td><td className="px-4 py-3 text-slate-600"><div>{row.matched_driver_name || row.system_driver_name || '-'}</div><div className="text-xs text-slate-400">{row.tractor_plate || '-'} {row.trailer_plate ? `/ ${row.trailer_plate}` : ''}</div></td><td className="max-w-md px-4 py-3 text-xs leading-5 text-slate-600">{row.description || '-'}</td><td className="px-4 py-3 text-slate-600">{formatDateTime(row.last_detected_at)}</td><td className="px-4 py-3 text-slate-600">{row.driver_viewed_at ? formatDateTime(row.driver_viewed_at) : '-'}</td><td className="px-4 py-3 text-slate-600">{row.check_count}</td></tr>)}</tbody></table></div>
      </div>
    </div>
  );
}
